/**
 * The ids of the Meta Pixel, Google Tag Manager, Google tag, ActiveCampaign
 * site-tracking and OpenAI Pixel Marketing Tags, each written into an inline
 * script on the tenant's site. The validators hold a newly entered id to its
 * real format; the sanitizers check it again where the id is written into the
 * script, so a value that reached the database some other way can never break
 * out of the script's string and inject code.
 *
 * - Meta Pixel: digits only (15 or 16 of them today). Meta documents the id
 *   only as the number in `fbq('init', …)`, so its length is held only to the
 *   cap every id shares.
 * - Google Tag Manager: the container id, `GTM-` then capital letters and
 *   numbers.
 * - Google tag: `G-` (Google Analytics), `GT-` (Google tag), `AW-` (Google
 *   Ads) or `DC-` (Floodlight), then capital letters and numbers. A
 *   Universal Analytics `UA-` id is refused: the Google tag does not run one.
 * - ActiveCampaign: no documented format, so its validator holds the id only
 *   to letters, numbers and dashes. Its sanitizer is the one it shipped with
 *   (see `sanitizeActiveCampaignAccountId`).
 * - OpenAI Pixel: OpenAI documents the id only by example (a run of digits),
 *   so it is held to the characters one can plausibly use: letters, numbers,
 *   dashes and underscores.
 *
 * Whitespace and quote marks a paste leaves around an id, and invisible marks
 * anywhere in it, are not part of it: `trimMarketingTagId` drops them on
 * save, the validators look past them (the admin form validates before any
 * hook runs), and the sanitizers drop them before the id is written.
 *
 * A save that leaves the stored id unchanged passes, whatever it is: the first
 * four fields stored free text before the validators existed (the OpenAI Pixel
 * ID had a looser one, which let a value of dashes alone through), and an id
 * that predates them must not block every other edit to its tenant. It still
 * goes through the sanitizer before it reaches a page. Payload passes the
 * stored value as `previousValue`, both when the document is saved and when
 * the admin form validates (`Tenants/marketing-tag-ids.wiring.test.ts` pins
 * the save). Restoring a version passes too (`isRestoringVersion`).
 */

import {
  isRestoringVersion,
  storedText,
  submittedText,
  type SubmittedText,
  type VersionRestoreRequest,
} from './fields/stored-value-validation';

/**
 * The invisible marks a copy from a web page, chat or desktop app can carry:
 * the soft hyphen, zero-width spaces and joiners, the word joiner and the
 * invisible operators beside it, the combining grapheme joiner, the Mongolian
 * vowel separator and free variation selectors, the variation selectors (a
 * chat app can leave U+FE0F after a character it offers as an emoji), the
 * Hangul fillers, the byte-order mark, and all three direction marks
 * (left-to-right, right-to-left and the Arabic letter mark), embeddings and
 * isolates (Windows apps wrap copied text in U+202A … U+202C). They are
 * dropped wherever they sit: nobody can see one, so the id left is exactly
 * the one on screen.
 */
const INVISIBLE_MARKS =
  /[\u00AD\u034F\u061C\u115F\u1160\u180B-\u180F\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\u3164\uFE00-\uFE0F\uFEFF\uFFA0]/g;

/**
 * Whitespace and quote marks: straight, curly, the low opening quotes German
 * and other European keyboards type („ ‚) and angle quotes (« » ‹ ›). They are
 * visible, so only dropped from the ends of a value.
 */
const EDGE_DEBRIS_CHARACTER = /[\s'"`‘’“”„‚«»‹›]/;

/**
 * Cleans a pasted value: drops the invisible marks anywhere in it and the
 * whitespace and quote marks at either end. It walks in from each end rather
 * than matching `[…]+$`, which retries from every position in a run of debris
 * inside a value: quadratic, so one long submitted value could stall the
 * server on save and on every admin form validation.
 */
const trimPasteDebris = (pasted: string): string => {
  const value = pasted.replace(INVISIBLE_MARKS, '');
  let start = 0;
  let end = value.length;
  while (start < end && EDGE_DEBRIS_CHARACTER.test(value.charAt(start))) start += 1;
  while (end > start && EDGE_DEBRIS_CHARACTER.test(value.charAt(end - 1))) end -= 1;
  return value.slice(start, end);
};

/**
 * No real id is a third this long. A custom `validate` replaces Payload's
 * default text validation, and with it the `defaultMaxTextLength` cap.
 */
const MAX_ID_LENGTH = 64;

const TOO_LONG_MESSAGE = `That is too long for one ID (${MAX_ID_LENGTH} characters at most): paste just the one ID.`;

/**
 * A `beforeValidate` hook, typed on the one argument it reads. It drops the
 * paste debris, and turns an id a REST write sent as a JSON number into its
 * digits, so the hooks after it and the operation's result see the string
 * Mongoose stores, not a number. A number past `Number.MAX_SAFE_INTEGER` is
 * left as it came, for the validator to refuse (`submittedIdText`).
 */
export const trimMarketingTagId = ({
  value,
}: {
  value?: string | number | null;
}): string | number | null | undefined => {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? String(value) : value;
  return typeof value === 'string' ? trimPasteDebris(value) : value;
};

type MarketingTagIdRule = {
  pattern: RegExp;
  message: string;
};

type MarketingTagIdValidator = (
  value: SubmittedText,
  // `unknown`: Payload types it as the field's value, but passes whatever is
  // stored (`storedIdText`).
  options?: { previousValue?: unknown; req?: VersionRestoreRequest },
) => true | string;

/**
 * The submitted id as text, or `null` when it cannot be one. A REST write can
 * send the id as a JSON number; on save `trimMarketingTagId` has already
 * turned a safe one into its digits, but the validator does not rely on the
 * hook. Only a number JSON carried exactly counts: past
 * `Number.MAX_SAFE_INTEGER` it was rounded on parse, so a 16-digit Pixel ID
 * would be stored as a different one. Anything else (an array, a boolean) is
 * no id, and is refused here rather than by a Mongoose cast error.
 */
const submittedIdText = (value: SubmittedText): string | null =>
  typeof value === 'number' && Number.isSafeInteger(value) ? String(value) : submittedText(value);

/**
 * A stored id as text: a number as its digits, anything else as `storedText`
 * reads it. The generated types say these fields hold strings, but one written
 * outside Payload (a raw import, a migration) may be a number or no text at
 * all. Like `storedText` it never throws, which at render also keeps every
 * page of the site up. The validator and `readMarketingTagIds` both read
 * stored ids through it, so a save and a page agree on one.
 */
export const storedIdText = (stored: unknown): string =>
  typeof stored === 'number' ? String(stored) : storedText(stored);

const marketingTagIdValidator = ({ pattern, message }: MarketingTagIdRule): MarketingTagIdValidator =>
  (value, { previousValue, req } = {}) => {
    if (isRestoringVersion(req)) return true;

    const text = submittedIdText(value);
    if (text === null) return message;

    const id = trimPasteDebris(text);
    if (!id || id === trimPasteDebris(storedIdText(previousValue))) return true;
    if (id.length > MAX_ID_LENGTH) return TOO_LONG_MESSAGE;
    if (pattern.test(id)) return true;

    return message;
  };

export const validateMetaPixelId = marketingTagIdValidator({
  pattern: /^\d+$/,
  message: 'Paste the Pixel ID from Meta Events Manager: numbers only, with no spaces.',
});

export const validateGtmId = marketingTagIdValidator({
  pattern: /^GTM-[A-Z0-9]+$/,
  message: 'Paste the container ID from Google Tag Manager: GTM- followed by capital letters and numbers only, e.g. GTM-ABC1234.',
});

export const validateGoogleTagId = marketingTagIdValidator({
  pattern: /^(?:G|GT|AW|DC)-[A-Z0-9]+$/,
  message: 'Paste the Google tag ID: G-, GT-, AW- or DC- followed by capital letters and numbers only, e.g. G-ABC123DEF4.',
});

/**
 * The characters the Google sanitizers accept and the ActiveCampaign
 * sanitizer keeps. One constant, so the ActiveCampaign validator cannot drift
 * from the characters its sanitizer keeps.
 */
const LETTERS_NUMBERS_AND_DASHES = 'A-Za-z0-9-';

/**
 * At least one letter or number: a value of dashes alone (`-`, typed to mean
 * "none") names no account, yet it would still count as a Marketing Tag and
 * raise the cookie banner for every visitor. The sanitizers drop one too, so
 * one stored before this rule (which a save leaves alone) does neither.
 */
const LETTER_OR_NUMBER = /[A-Za-z0-9]/;

/**
 * A whole id made of `characters` and holding a letter or number. The
 * validators that accept a set of characters and the sanitizers share it, so
 * no id a validator accepts can be one its sanitizer drops.
 */
const wholeIdPattern = (characters: string): RegExp =>
  new RegExp(`^(?=.*${LETTER_OR_NUMBER.source})[${characters}]+$`);

export const validateActiveCampaignAccountId = marketingTagIdValidator({
  pattern: wholeIdPattern(LETTERS_NUMBERS_AND_DASHES),
  message: 'Paste the account ID from Settings > Tracking > Site Tracking in ActiveCampaign: letters, numbers and dashes only.',
});

const OPENAI_PIXEL_ID_CHARACTERS = 'A-Za-z0-9_-';

export const validateOpenAiPixelId = marketingTagIdValidator({
  pattern: wholeIdPattern(OPENAI_PIXEL_ID_CHARACTERS),
  message: 'Paste the Pixel ID from the Conversions tab in OpenAI Ads Manager: letters, numbers, dashes and underscores only.',
});

/**
 * Returns the id with the paste debris dropped, or `''` when anything left is
 * a character no id of the kind contains, or nothing is left but dashes or
 * underscores (`LETTER_OR_NUMBER`). It never deletes a visible character from
 * inside a value: what is left of `Pixel 2: 1234…` could be another
 * advertiser's id, and the page would send its visitors' data there.
 */
const marketingTagIdSanitizer = (characters: string) => {
  const wholeId = wholeIdPattern(characters);

  return (value: string): string => {
    const id = trimPasteDebris(value);
    return wholeId.test(id) ? id : '';
  };
};

export const sanitizeMetaPixelId = marketingTagIdSanitizer('0-9');

/**
 * Keeps lower case, which the Google validators refuse, so an id stored
 * before they existed still reaches the page.
 */
const sanitizeLettersNumbersAndDashes = marketingTagIdSanitizer(LETTERS_NUMBERS_AND_DASHES);

export const sanitizeGtmId = sanitizeLettersNumbersAndDashes;

export const sanitizeGoogleTagId = sanitizeLettersNumbersAndDashes;

export const sanitizeOpenAiPixelId = marketingTagIdSanitizer(OPENAI_PIXEL_ID_CHARACTERS);

const NOT_LETTER_NUMBER_OR_DASH = new RegExp(`[^${LETTERS_NUMBERS_AND_DASHES}]`, 'g');

/**
 * Applied where `ActiveCampaignScript` writes the account id and in
 * `RenderLayout`, so an id with nothing usable left is no Marketing Tag.
 *
 * Unlike the others it deletes characters from inside the value, as it did
 * before any of these ids were validated. The other three were raw text in
 * their scripts, so a stray character inside one broke its tag, and dropping
 * the id loses nothing. One case did load and is no longer supported: Tag
 * Manager environment parameters pasted after the container id
 * (`GTM-…&gtm_auth=…&gtm_preview=…`), which the loader URL happened to carry.
 * The field holds the container id, and an environment is for testing a
 * container version before it is published, not for a live site. An
 * ActiveCampaign id stored with a stray character has always rendered with it
 * stripped, and refusing it now would silently stop that tenant's live
 * tracking. Its validator keeps any id saved from now on to the characters
 * kept here, so only an older one is joined. What is left must still hold a
 * letter or number (`LETTER_OR_NUMBER`).
 */
export const sanitizeActiveCampaignAccountId = (value: string): string => {
  const id = value.replace(NOT_LETTER_NUMBER_OR_DASH, '');
  return LETTER_OR_NUMBER.test(id) ? id : '';
};
